import { useId } from "react";

/**
 * The product mark: the football from `public/idkf-logo.png`, redrawn as
 * vector so it stays crisp at a 16px favicon and a 200px hero alike.
 *
 * Geometry and colours were measured off the source PNG rather than
 * guessed (semi-axes 40 : 24.5 in this 64-unit box, rotated 45 degrees,
 * seams at 68% of the way to each tip). The two-tone shading is the whole
 * character of the mark: everything above the long axis is the lighter
 * leather, everything below it the darker, including the seams and laces.
 * That is why the shape is drawn twice, once clipped to each half, rather
 * than as one ball with a shadow on top.
 *
 * The colours are literal, not tokens. This is a brand asset: it must look
 * the same in light mode, in dark mode, and on the cream of an app icon.
 */

const LIGHT_LEATHER = "#a24603";
const DARK_LEATHER = "#6f2e01";
const LIGHT_SEAM = "#f6efea";
const DARK_SEAM = "#eee1dc";

/** Laces: a spine along the long axis with five crossbars. */
const TICKS = [18, 25, 32, 39, 46];

const FootballMark = ({ className = "", title }) => {
  /* Two instances of the mark can share a page (header and footer), so the
     clip ids have to be unique per render. */
  const uid = useId().replace(/:/g, "");
  const ball = `${uid}-ball`;
  const top = `${uid}-top`;
  const bottom = `${uid}-bottom`;
  const seams = `${uid}-seams`;

  return (
    <svg
      className={className}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      role={title ? "img" : "presentation"}
      aria-label={title}
      aria-hidden={title ? undefined : "true"}
      focusable="false"
    >
      <defs>
        <clipPath id={ball}>
          <ellipse cx="32" cy="32" rx="40" ry="24.5" />
        </clipPath>
        <clipPath id={top}>
          <rect x="-24" y="-24" width="112" height="56" />
        </clipPath>
        <clipPath id={bottom}>
          <rect x="-24" y="32" width="112" height="56" />
        </clipPath>

        {/* Seams and laces, drawn once and inked twice. */}
        <g id={seams}>
          <rect x="2.9" y="-24" width="3.8" height="112" />
          <rect x="57.3" y="-24" width="3.8" height="112" />
          <rect x="15" y="30.1" width="34" height="3.8" rx="1.9" />
          {TICKS.map((x) => (
            <rect
              key={x}
              x={x - 1.9}
              y="26.5"
              width="3.8"
              height="11"
              rx="1.9"
            />
          ))}
        </g>
      </defs>

      <g transform="rotate(-45 32 32)" clipPath={`url(#${ball})`}>
        <g clipPath={`url(#${top})`}>
          <rect x="-24" y="-24" width="112" height="112" fill={LIGHT_LEATHER} />
          <use href={`#${seams}`} fill={LIGHT_SEAM} />
        </g>
        <g clipPath={`url(#${bottom})`}>
          <rect x="-24" y="-24" width="112" height="112" fill={DARK_LEATHER} />
          <use href={`#${seams}`} fill={DARK_SEAM} />
        </g>
      </g>
    </svg>
  );
};

export default FootballMark;
